/**
 * Authentication and Authorization Type Definitions
 * Multi-Level Access Control Trading Platform (MLACT)
 */

import { WatchlistProps } from "./Types";

// Security Levels
export enum SecurityLevel {
    PUBLIC = 0,           // Level 0 - No authentication required
    DEMO = 1,             // Level 1 - authentication required but not client
    CLIENT = 2,           // Level 2 - Standard client login
    INTERNAL = 3          // Level 3 - Internal/Admin with role-based access
}

// User Roles for Level 2 (Internal/Admin)
export enum UserRole {
    // Public users
    PUBLIC_USER = "public_user",

    // Authenticated clients (Level 1)   
    DEMO_CLIENT = "demo_client",
   
    // Authenticated clients (Level 2)
    CLIENT = "client",  
    SUPER_CLIENT = "super_client",

    // Internal staff (Level 3)
    HR_USER = "hr_user",
    COMPLIANCE_USER = "compliance_user",
    ACCOUNTANT_USER = "accountant",
    RESEARCH_USER = "research_team",
    SIGMA_PLUS_USER = "sigma_plus",
    SUPER_ADMIN = "super_admin",
}

// Permission types for fine-grained access control
export enum Permission {
    // Market Permissions
    VIEW_MARKET = 'view:market',
    VIEW_MARKET_SUMMARY = "view:market:summary",
    VIEW_MARKET_SUMMARY_PERFORMANCE = "view:market:summary:performance",
    VIEW_MARKET_SUMMARY_PERFORMANCE_1 = "view:market:summary:performance:1",
    VIEW_MARKET_SUMMARY_PERFORMANCE_7 = "view:market:summary:performance:7",
    VIEW_MARKET_SUMMARY_PERFORMANCE_30 = "view:market:summary:performance:30",
    VIEW_MARKET_SUMMARY_BREADTH = "view:market:summary:breadth",
    VIEW_MARKET_SUMMARY_BREADTH_1 = "view:market:summary:breadth:1",
    VIEW_MARKET_SUMMARY_BREADTH_7 = "view:market:summary:breadth:7",
    VIEW_MARKET_SUMMARY_BREADTH_30 = "view:market:summary:breadth:30",
    VIEW_MARKET_SUMMARY_BREADTH_365 = "view:market:summary:breadth:365",
    VIEW_MARKET_SUMMARY_BREADTH_370 = "view:market:summary:breadth:370",
    VIEW_MARKET_SUMMARY_INVESTORS = "view:market:summary:investors",
    VIEW_MARKET_SUMMARY_INVESTORS_1 = "view:market:summary:investors:1",
    VIEW_MARKET_SUMMARY_INVESTORS_7 = "view:market:summary:investors:7",
    VIEW_MARKET_SUMMARY_INVESTORS_30 = "view:market:summary:investors:30",
    VIEW_MARKET_SUMMARY_INVESTORS_365 = "view:market:summary:investors:365",
    VIEW_MARKET_SUMMARY_INVESTORS_370 = "view:market:summary:investors:370",
    VIEW_MARKET_SUMMARY_SECTORS = "view:market:summary:sectors",
    VIEW_MARKET_SUMMARY_SECTORS_1 = "view:market:summary:sectors:1",
    VIEW_MARKET_SUMMARY_SECTORS_7 = "view:market:summary:sectors:7",
    VIEW_MARKET_SUMMARY_SECTORS_30 = "view:market:summary:sectors:30",
    VIEW_MARKET_SUMMARY_SECTORS_365 = "view:market:summary:sectors:365",
    VIEW_MARKET_SUMMARY_SECTORS_370 = "view:market:summary:sectors:370",
    VIEW_MARKET_RESEARCH = "view:market:research",
    //VIEW_MARKET_RESEARCH_TECHNICAL_SWINGTRADES = "view:market:research:technical_swingtrades",
    //VIEW_MARKET_RESEARCH_TECHNICAL_PIVOTPOINTS = "view:market:research:technical_pivotpoints",
    VIEW_MARKET_NEWS = "view:market:news",
    VIEW_MARKET_SECTORS = "view:market:sectors",
    VIEW_MARKET_CALENDAR = "view:market:calendar",
    VIEW_MARKET_WATCHLIST = "view:market:watchlist",
    VIEW_MARKET_QUOTE_DETAILS = "view:market:quote-details",

    // Account Permissions
    VIEW_ACCOUNT = 'view:account',
    VIEW_ACCOUNT_OVERVIEW = "view:account:overview",
    VIEW_ACCOUNT_POSITIONS = "view:account:positions",
    VIEW_ACCOUNT_ACTIVITY = "view:account:activity",
    VIEW_ACCOUNT_WALLET = "view:account:wallet",
    VIEW_ACCOUNT_ORDERS_HISTORY = "view:account:orders-history",

    // Trade Permissions
    VIEW_TRADE = "view:trade",
    VIEW_TRADE_TICKET = "view:trade:ticket",
    VIEW_TRADE_LIVE_ORDERS = "view:trade:live-orders",
    VIEW_TRADE_HISTORY = "view:trade:history",
    EXECUTE_TRADE = "execute:trade",

    // Internal/Admin Permissions
    VIEW_ACCOUNTANT_REPORTS = "view:admin:accountant-reports",
    VIEW_HR_SECTION = "view:admin:hr-section",
    VIEW_COMPLIANCE_TOOLS = "view:admin:compliance-tools",
    VIEW_RESEARCH_SECTION = "view:admin:research-section",
    VIEW_SIGMA_PLUS_SECTION = "view:admin:sigma-plus-section",

    // Data Permissions
    EDIT_DATA = "edit:data",
    DELETE_DATA = "delete:data",
    EXPORT_DATA = "export:data",
    VIEW_DATA = "view:data",

    // Admin Permissions
    MANAGE_USERS = "manage:users",
    MANAGE_ROLES = "manage:roles",
    VIEW_AUDIT_LOGS = "view:audit-logs",
    MANAGE_SYSTEM_SETTINGS = "manage:system-settings"
}

//UserType
export type UserType = 'p' | 'd' | 'c' | 'i';

// User authentication state
export interface AuthUser {
    id: number | string;
    username: string;
    sessionId: string;
    securityLevel: SecurityLevel;
    roles: UserRole[];
    permissions: Permission[];
    mainAccount?: any; // Your AccountInfo type
    otherAccounts?: any[];
    authenticated: boolean;
    userType: UserType; // 'c' | 'i'; // 'c' for client, 'i' for internal

    // User account type flags (from getUserMetaData)
    marginUser?: boolean;           // Has margin trading enabled (acc_type includes 1)
    sameDayUser?: boolean;          // Has same-day trading enabled (acc_type includes 2)
    treasuryBillsUser?: boolean;    // Has treasury bills access (acc_type includes 3)
    mutualFundsUser?: boolean;      // Has mutual funds access (acc_type includes 4)
    isDemoAccount?: boolean;        // Is this a demo/practice account

    // User watchlist (from getUserMetaData)
    websiteWatchlist?: WatchlistProps[];      // User's watchlist configurations
}

// Authentication context state
export interface AuthState {
    user: AuthUser | null;
    isAuthenticated: boolean;
    requestedSecretWord: boolean;
    isLoading: boolean;
    error: string | null;
    secretWordSequence: string[] | null,
    hintQuestion: string | null,
    userName: string | null
}

// Route access requirements
export interface RouteAccess {
    minSecurityLevel: SecurityLevel;
    requiredRoles?: UserRole[];
    requiredPermissions?: Permission[];
    requireAll?: boolean; // If true, user must have ALL permissions; if false, ANY permission
}

// Navigation item configuration
export interface NavigationItem {
    id: string;
    label: string;
    path: string;
    icon?: string;
    access: RouteAccess;
    children?: NavigationItem[];
}

// API Response Types
export interface LoginResponse {
    result: number;
    response: {
        message?: string;
        data?: {
            userSession: string;
            usertype: 'c' | 'i';
            roles?: string[];
            logedCustomerinfo?: any;
            otherAccounts?: any[];
        };
    };
}

export interface SecretWordResponse {
    result: number;
    response: {
        message?: string;
        data?: {
            requiredLetter: string[];
            userHint: string;
        };
    };
}

// Audit log entry
export interface AuditLogEntry {
    timestamp: Date;
    userId: string | number;
    action: string;
    resource: string;
    success: boolean;
    ipAddress?: string;
    userAgent?: string;
}